import React, { useEffect, useState } from 'react';
import { getLogs, ApiLog } from '@api/logs';

interface LogFilterState {
    message: string;
    user: string;
    minLevel: string;
    fromDate: string;
    toDate: string;
}

const levelOptions = [
    { value: '', label: 'All' },
    { value: '0', label: 'Trace' },
    { value: '1', label: 'Debug' },
    { value: '2', label: 'Information' },
    { value: '3', label: 'Warning' },
    { value: '4', label: 'Error' },
    { value: '5', label: 'Critical' },
];

const inputStyle: React.CSSProperties = {
    padding: '4px 6px',
    fontSize: 14,
    borderRadius: 4,
    border: '1px solid #cfd8dc',
    background: '#f7fafd',
};

const buttonPrimary: React.CSSProperties = {
    padding: '6px 14px',
    borderRadius: 6,
    background: '#4e6e8e',
    color: '#fff',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
};

const buttonSecondary: React.CSSProperties = {
    ...buttonPrimary,
    background: '#e0e6ed',
    color: '#123',
    fontWeight: 500,
};

export default function LogsAdmin() {
    const [logs, setLogs] = useState<ApiLog[]>([]);
    const [filter, setFilter] = useState<LogFilterState>({
        message: '',
        user: '',
        minLevel: '',
        fromDate: '',
        toDate: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(200);

    useEffect(() => {
        loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadLogs() {
        setLoading(true);
        setError(null);
        try {
            const data = await getLogs({
                filtermessage: filter.message || undefined,
                filteruser: filter.user || undefined,
                minlevel: filter.minLevel !== '' ? Number(filter.minLevel) : undefined,
                fromdate: filter.fromDate || undefined,
                todate: filter.toDate || undefined,
                skip: pageIndex * pageSize,
                take: pageSize,
            });
            setLogs(data ?? []);
        } catch (e: any) {
            setError(e.message || 'Error loading logs');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }

    function handleFilterChange(
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) {
        const { name, value } = e.target;
        setFilter((prev) => ({ ...prev, [name]: value }));
    }

    function handleFilterKeyDown(e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) {
        if (e.key === 'Enter') {
            e.preventDefault();
            setPageIndex(0);
            loadLogs();
        }
    }

    // Helpers
    function formatTimestamp(value: string | undefined) {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleString();
    }

    function mapLevel(level: number): string {
        switch (level) {
            case 0:
                return 'Trace';
            case 1:
                return 'Debug';
            case 2:
                return 'Info';
            case 3:
                return 'Warn';
            case 4:
                return 'Error';
            case 5:
                return 'Critical';
            default:
                return String(level);
        }
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(90vh / var(--ui-scale))',
                border: '1px solid #ccc',
                borderRadius: 12,
                overflow: 'hidden',
                background: '#f7fafd',
            }}
        >
            {/* Filters bovenin */}
            <div
                style={{
                    padding: '10px 16px',
                    borderBottom: '1px solid #e0e6ed',
                    background: '#fff',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    alignItems: 'flex-end',
                }}
            >
                <div style={{ paddingRight: 10, minWidth: 180, maxWidth: 260 }}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>Message</div>
                    <input
                        name="message"
                        value={filter.message}
                        onChange={handleFilterChange}
                        onKeyDown={handleFilterKeyDown}
                        style={{ ...inputStyle, width: '100%' }}
                        placeholder="contains..."
                    />
                </div>
                <div style={{ paddingRight: 10, minWidth: 160, maxWidth: 220 }}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>User</div>
                    <input
                        name="user"
                        value={filter.user}
                        onChange={handleFilterChange}
                        onKeyDown={handleFilterKeyDown}
                        style={{ ...inputStyle, width: '100%' }}
                        placeholder="user name"
                    />
                </div>
                <div style={{ paddingRight: 10, minWidth: 140, maxWidth: 180 }}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>Min level</div>
                    <select
                        name="minLevel"
                        value={filter.minLevel}
                        onChange={handleFilterChange}
                        style={{ ...inputStyle, width: '100%' }}
                    >
                        {levelOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={{ paddingRight: 10, minWidth: 150, maxWidth: 180 }}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>From</div>
                    <input
                        type="date"
                        name="fromDate"
                        value={filter.fromDate}
                        onChange={handleFilterChange}
                        style={{ ...inputStyle, width: '100%' }}
                    />
                </div>
                <div style={{ paddingRight: 10, minWidth: 150, maxWidth: 180 }}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>To</div>
                    <input
                        type="date"
                        name="toDate"
                        value={filter.toDate}
                        onChange={handleFilterChange}
                        style={{ ...inputStyle, width: '100%' }}
                    />
                </div>

                {/* Page size + navigatie */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>Page size</div>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value) || 50);
                            setPageIndex(0);
                        }}
                        style={{ ...inputStyle, width: 80 }}
                    >
                        {[20, 50, 100, 200].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                </div>

                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        marginLeft: 'auto',
                        alignItems: 'flex-end',
                    }}
                >
                    <button
                        type="button"
                        style={buttonPrimary}
                        onClick={() => {
                            setPageIndex(0);
                            loadLogs();
                        }}
                        disabled={loading}
                    >
                        Search
                    </button>
                    <button
                        type="button"
                        style={buttonSecondary}
                        onClick={() => {
                            setFilter({
                                message: '',
                                user: '',
                                minLevel: '',
                                fromDate: '',
                                toDate: '',
                            });
                            setLogs([]);
                            setPageIndex(0);
                        }}
                        disabled={loading}
                    >
                        Clear
                    </button>
                </div>
            </div>

            {error && (
                <div style={{ padding: '8px 16px', color: 'red' }}>{error}</div>
            )}

            {/* Scrollbare grid */}
            <div
                style={{
                    flex: 1,
                    overflow: 'auto',
                    background: '#f7fafd',
                }}
            >
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 13,
                    }}
                >
                    <thead>
                        <tr style={{ background: '#eaf1f7' }}>
                            <th
                                style={{
                                    padding: '6px 8px',
                                    borderBottom: '1px solid #e0e6ed',
                                    textAlign: 'left',
                                }}
                            >
                                Timestamp
                            </th>
                            <th
                                style={{
                                    padding: '6px 8px',
                                    borderBottom: '1px solid #e0e6ed',
                                    textAlign: 'left',
                                }}
                            >
                                Level
                            </th>
                            <th
                                style={{
                                    padding: '6px 8px',
                                    borderBottom: '1px solid #e0e6ed',
                                    textAlign: 'left',
                                }}
                            >
                                User
                            </th>
                            <th
                                style={{
                                    padding: '6px 8px',
                                    borderBottom: '1px solid #e0e6ed',
                                    textAlign: 'left',
                                    width: '60%',
                                }}
                            >
                                Message
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => {
                            const isSelected = log.id === selectedLogId;

                            return (
                                <tr
                                    key={log.id}
                                    onClick={() =>
                                        setSelectedLogId((prev) =>
                                            prev === log.id ? null : log.id,
                                        )
                                    }
                                    style={{
                                        cursor: 'pointer',
                                        background: isSelected ? '#f0f7ff' : 'transparent',
                                    }}
                                >
                                    {/* timestamp from logDate */}
                                    <td
                                        style={{
                                            padding: '4px 8px',
                                            borderBottom: '1px solid #f0f4f7',
                                            verticalAlign: 'top',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {formatTimestamp(log.timestamp)}
                                    </td>

                                    {/* logLevel */}
                                    <td
                                        style={{
                                            padding: '4px 8px',
                                            borderBottom: '1px solid #f0f4f7',
                                            verticalAlign: 'top',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {log.logLevelName ?? mapLevel(log.logLevel)}
                                    </td>

                                    {/* userName */}
                                    <td
                                        style={{
                                            padding: '4px 8px',
                                            borderBottom: '1px solid #f0f4f7',
                                            verticalAlign: 'top',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {log.user ?? ''}
                                    </td>

                                    {/* message, met wordwrap */}
                                    <td
                                        style={{
                                            padding: '4px 8px',
                                            borderBottom: '1px solid #f0f4f7',
                                            verticalAlign: 'top',
                                            // standaard: 1 regel, ellipsis
                                            // geselecteerd: volledige tekst met word-wrap
                                            whiteSpace: isSelected ? 'pre-wrap' : 'nowrap',
                                            overflow: isSelected ? 'visible' : 'hidden',
                                            textOverflow: isSelected ? 'clip' : 'ellipsis',
                                        }}
                                    >
                                        {log.message}
                                    </td>
                                </tr>
                            );
                        })}
                        {logs.length === 0 && !loading && (
                            <tr>
                                <td
                                    colSpan={5}
                                    style={{
                                        padding: 16,
                                        textAlign: 'center',
                                        color: '#888',
                                    }}
                                >
                                    No logs
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paging controls */}
            <div
                style={{
                    padding: '6px 16px 10px',
                    borderTop: '1px solid #e0e6ed',
                    background: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 12,
                }}
            >
                <div>
                    Page {pageIndex + 1} · Showing {logs.length} item(s)
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        type="button"
                        style={buttonSecondary}
                        onClick={() => {
                            if (pageIndex > 0) {
                                const newIndex = pageIndex - 1;
                                setPageIndex(newIndex);
                                // na setState is direct loadLogs() prima, omdat loadLogs de actuele state leest
                                loadLogs();
                            }
                        }}
                        disabled={loading || pageIndex === 0}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        style={buttonSecondary}
                        onClick={() => {
                            if (logs.length === pageSize) {
                                const newIndex = pageIndex + 1;
                                setPageIndex(newIndex);
                                loadLogs();
                            }
                        }}
                        disabled={loading || logs.length < pageSize}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}